import { clampTargetPolycountPatch } from 'Pods/ThreeDStudio/Services/clampTargetPolycountPatch'
import { findMatchingUseCase } from 'Pods/ThreeDStudio/Services/findMatchingUseCase'
import { hasPolycountField } from 'Pods/ThreeDStudio/Services/hasPolycountField'
import { polyCountForValue } from 'Pods/ThreeDStudio/Services/polyCountForValue'
import { resolveActivePolycountRange } from 'Pods/ThreeDStudio/Services/resolveActivePolycountRange'
import {
  getGenerationSchemaForProvider,
  getNextGenerationSchema,
} from 'Pods/ThreeDStudio/Shared/Utils/getNextGenerationSchema'
import { getProviderSettingsPatch } from 'Pods/ThreeDStudio/Shared/Utils/getProviderSettingsPatch'

import {
  DEFAULT_MODEL_SETTINGS,
  POLY_COUNT,
  DEFAULT_VIEWER,
  DEFAULT_REMESH_SETTINGS,
  DEFAULT_SCHEMA,
  DEFAULT_GENERATION,
  DEFAULT_UI,
  ACTION,
  IMAGE_ASSISTANT_TAB,
} from '../constants'
import { threeDStudioStore } from '../index'
import { activeRunStateOf } from './generation'
import { setGenerationProvider } from './schema'
import { selectIsUnlimitedPolycount } from '../selectors/selectIsUnlimitedPolycount'

const setModelSettingsState = (patch) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...state.modelSettings, ...patch },
  }))
}

// generationModelSchema overrides the stored schema when the match must see the one an
// engine switch resolves after this render.
const setModelSettingsStateMatchingUseCase = (patch, generationModelSchema) => {
  threeDStudioStore.setState((state) => {
    const modelSettings = { ...state.modelSettings, ...patch }
    const schema = generationModelSchema ? { ...state.schema, generationModelSchema } : state.schema
    const { polygonType, isSmartLowPoly } = modelSettings
    const range = resolveActivePolycountRange(schema.generationModelSchema, { polygonType, isSmartLowPoly })
    // Engines with polycount_mode match the effective flag (smart low poly forces a
    // target); engines without it hide the toggle, so the match leaves the flag out.
    const polycountModeSupported = Boolean(schema.generationModelSchema?.input_schema?.properties?.polycount_mode)
    const isUnlimitedPolycount = polycountModeSupported
      ? selectIsUnlimitedPolycount({ ...state, modelSettings, schema })
      : undefined
    const selectedUseCase = findMatchingUseCase({ ...modelSettings, isUnlimitedPolycount }, range)

    return { modelSettings: { ...modelSettings, selectedUseCase } }
  })
}

/**
 * Selects the use case whose preset the current model settings match, if any. The
 * defaults preselect none, so useActiveModelSchema runs this whenever the generation
 * schema resolves from empty: page load and the resets that restore the defaults.
 */
export const selectMatchingUseCase = () => {
  setModelSettingsStateMatchingUseCase({})
}

/**
 * Resets all model settings to their default values.
 */
export const clearModelSettings = () => {
  threeDStudioStore.setState((state) => ({
    modelSettings: { ...DEFAULT_MODEL_SETTINGS },
    viewer: { ...DEFAULT_VIEWER },
    remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
    schema: { ...DEFAULT_SCHEMA },
    ui: {
      ...state.ui,
      openedModel: {},
      isModelSettingsOpen: true,
      isViewerSidebarOpen: false,
      activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
      // The assistant belonged to the composition being discarded, so it closes with it.
      // This action empties openedModel itself, so the URL-sync clear that follows the
      // navigation to /ai is skipped by its own guard - the reset has to happen here.
      isImageAssistantOpen: false,
      imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
      editImage: null,
      // The new composition brings its own session; restoring the previous conversation
      // over it would carry the discarded model's images into the new panel.
      imageAssistantSessionId: undefined,
      isImageAssistantPrivate: false,
    },
  }))
}

/**
 * Sets the prompt
 * @param {string} prompt - User prompt for generation
 */
export const setPrompt = (prompt) => {
  setModelSettingsState({ prompt })
}

/**
 * Sets the polygon count
 * @param {string} polyCount - One of: "low", "medium", "high"
 * @param {boolean} skipSelectedUseCaseUpdate - Whether to skip updating the selected use case
 */
export const setPolyCount = (polyCount, skipSelectedUseCaseUpdate = false) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, isSmartLowPoly } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  if (range) {
    const { minimum, maximum, default: defaultValue } = range
    const targetPolyCount = {
      [POLY_COUNT.LOW]: minimum,
      [POLY_COUNT.MEDIUM]: defaultValue,
      [POLY_COUNT.HIGH]: maximum,
    }[polyCount]

    if (skipSelectedUseCaseUpdate === true) {
      setModelSettingsState({ polyCount, targetPolycount: targetPolyCount })

      return
    }

    setModelSettingsStateMatchingUseCase({ polyCount, targetPolycount: targetPolyCount })

    return
  }

  setModelSettingsStateMatchingUseCase({ polyCount })
}

/**
 * Sets the generate type
 * @param {string} generateType - One of: "normal", "lowPoly" or undefined
 */
export const setGenerateType = (generateType) => {
  setModelSettingsState({ generateType })
}

/**
 * Sets the material type
 * @param {string} material - One of: "pbr", "basic", "geometry_only"
 */
export const setMaterial = (material) => {
  setModelSettingsStateMatchingUseCase({ material })
}

/**
 * Sets the polygon type
 * @param {string} polygonType - One of: "quadrilateral", "triangle" or undefined
 */
export const setPolygonType = (polygonType) => {
  setModelSettingsStateMatchingUseCase({ polygonType })
}

/**
 * Sets the target polygon count
 * @param {number|undefined} targetPolycount - Number or empty string
 */
export const setTargetPolycount = (targetPolycount) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, isSmartLowPoly } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  // Gated on the field, not the range: a schema that declares a polycount but
  // resolves no bounds can match no button, so the label has to clear.
  let polyCount = modelSettings.polyCount
  if (hasPolycountField(schema?.generationModelSchema) && targetPolycount !== '' && targetPolycount != null) {
    polyCount = polyCountForValue(Number(targetPolycount), range)
  }

  setModelSettingsStateMatchingUseCase({ targetPolycount, polyCount })
}

/**
 * Sets the input image (asset ID). The single image only shows on the Single Image tab, so
 * setting one also leaves the multi-view uploader: "Use for 3D Generation" lands on it from
 * any image surface, whichever tab the form was left on.
 * @param {number|undefined} id - Input image asset id
 * @param {string|undefined} url - Input image URL (e.g. blob URL or file URL)
 */
export const setInputImage = (id, url) => {
  if (id === undefined) {
    threeDStudioStore.setState((state) => ({
      modelSettings: {
        ...state.modelSettings,
        inputImage: undefined,
        inputImageUrl: undefined,
      },
    }))

    return
  }

  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputImage: id,
      inputImageUrl: url,
      isMultiView: false,
    },
    schema: {
      ...state.schema,
      generationAction: ACTION.IMAGE_TO_3D,
    },
    generation: {
      ...state.generation,
      sessionId: undefined,
      runId: undefined,
    },
  }))
}

/**
 * Switches the image input between the single-image and multi-view uploaders.
 * @param {boolean} isMultiView
 */
export const setIsMultiView = (isMultiView) => {
  setModelSettingsState({ isMultiView })
}

/**
 * Sets one view of the multi-view input. The views are kept apart from the single-image
 * inputImage, so switching tabs never carries an upload from one uploader to the other.
 * @param {string} viewType - One of VIEW_TYPE
 * @param {number|undefined} id - Input image asset id; undefined removes the view
 * @param {string|undefined} url - Input image URL (e.g. blob URL or file URL)
 */
export const setInputViewImage = (viewType, id, url) => {
  if (id === undefined) {
    threeDStudioStore.setState((state) => {
      const { [viewType]: removed, ...inputViewImages } = state.modelSettings.inputViewImages

      return { modelSettings: { ...state.modelSettings, inputViewImages } }
    })

    return
  }

  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputViewImages: { ...state.modelSettings.inputViewImages, [viewType]: { id, url } },
    },
    schema: {
      ...state.schema,
      generationAction: ACTION.IMAGE_TO_3D,
    },
    generation: {
      ...state.generation,
      sessionId: undefined,
      runId: undefined,
    },
  }))
}

/**
 * Sets the input threeD (asset ID)
 * @param {number|undefined} id - Input threeD asset id
 * @param {string|undefined} url - Input threeD URL (e.g. blob URL or file URL)
 * @param {number|undefined} size - Input threeD size in bytes
 * @param {string|undefined} format - Input threeD format (e.g. "glb", "fbx", "obj", "stl")
 */
export const setInputThreeD = (id, url, size, format) => {
  threeDStudioStore.setState((state) => ({
    modelSettings: {
      ...state.modelSettings,
      inputThreeDUrl: url,
    },
    // Reset generation outcome state, but keep in-flight runs (and the scalar
    // mirror of the latest one) alive - concurrent generations must survive an
    // unrelated model upload.
    generation: { ...DEFAULT_GENERATION, ...activeRunStateOf(state.generation) },
    viewer: {
      ...state.viewer,
      inputThreeD: id,
      fileFormat: format,
      fileSize: size,
    },
  }))
}

/**
 * Sets the input threeD URL
 * @param {string|undefined} inputThreeDUrl - Input threeD URL (e.g. blob URL or file URL)
 */
export const setInputThreeDUrl = (inputThreeDUrl) => {
  setModelSettingsState({ inputThreeDUrl })
}

/**
 * Sets the input threeD format (file extension) for blob URLs
 * @param {string|undefined} inputThreeDFormat - One of: "glb", "fbx", "obj", "stl"
 */
export const setInputThreeDFormat = (inputThreeDFormat) => {
  setModelSettingsState({ inputThreeDFormat })
}

/**
 * Sets the private flag
 * @param {boolean} isPrivate - Whether the content should be private
 */
export const setIsPrivate = (isPrivate) => {
  setModelSettingsState({ isPrivate })
}

/**
 * Sets the smart low poly flag. Turning it off drops the polycount to Low, since the
 * low poly target is far below the full range and the tier it maps to there is
 * meaningless once the run is back on the standard model.
 * @param {boolean} isSmartLowPoly - Whether smart low poly is enabled
 */
export const setIsSmartLowPoly = (isSmartLowPoly) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const { polygonType, targetPolycount } = modelSettings
  const range = resolveActivePolycountRange(schema?.generationModelSchema, { polygonType, isSmartLowPoly })

  // The Polycount control clamps too, but it is unmounted while the settings
  // panel is collapsed - and that is where this toggle also lives.
  setModelSettingsStateMatchingUseCase({
    isSmartLowPoly,
    ...(isSmartLowPoly && { extraQuality: false, isUnlimitedPolycount: false }),
    ...clampTargetPolycountPatch(targetPolycount, range),
  })

  if (!isSmartLowPoly) {
    setPolyCount(POLY_COUNT.LOW)
  }
}

/**
 * Sets the unlimited polycount flag (Meshy polycount_mode). Smart low poly always runs on
 * a polygon target, so the two are mutually exclusive: enabling unlimited turns smart low
 * poly off through setIsSmartLowPoly, which also moves the polycount back to the standard range.
 * Only the Standard preset keeps unlimited on, so toggling it re-matches the preset.
 * @param {boolean} isUnlimitedPolycount - Whether the polycount is unlimited
 */
export const setIsUnlimitedPolycount = (isUnlimitedPolycount) => {
  if (isUnlimitedPolycount && threeDStudioStore.getState().modelSettings.isSmartLowPoly) {
    setIsSmartLowPoly(false)
  }

  setModelSettingsStateMatchingUseCase({ isUnlimitedPolycount })
}

/**
 * Sets the extra quality flag. No use case preset covers extra quality, so enabling it
 * takes the settings outside the selected preset and deselects it. It also turns smart
 * low poly off: the two are mutually exclusive, since the backend forces ultra_mode off
 * on LowPoly runs.
 * @param {boolean} extraQuality - Whether extra quality is enabled
 */
export const setExtraQuality = (extraQuality) => {
  setModelSettingsStateMatchingUseCase({
    extraQuality,
    ...(extraQuality && { isSmartLowPoly: false }),
  })
}

/**
 * Resets the model settings the given engine cannot run, so that an engine picked while
 * Smart low poly or Extra quality is on takes priority over those settings instead of
 * being disabled by them. The toggles hide themselves once the engine is active
 * (providerSupportsParam), so this reset is what keeps a hidden setting from staying on
 * behind the scenes. The use case is re-matched against the engine's own schema: the
 * settings may no longer match the selected preset, and whether unlimited polycount
 * applies depends on the engine. An out-of-range polycount is normalized by the schema
 * swap that follows in setGenerationModelSchema.
 * @param {string} provider - Provider id about to become the active engine
 */
export const resetSettingsUnsupportedByProvider = (provider) => {
  const { schema, modelSettings } = threeDStudioStore.getState()
  const patch = getProviderSettingsPatch(schema.generationAction, provider, modelSettings)
  const nextSchema = getGenerationSchemaForProvider(schema.generationAction, { ...modelSettings, ...patch }, provider)

  setModelSettingsStateMatchingUseCase(patch, nextSchema)
}

/**
 * Applies a use case preset to the store
 * @param {Object} useCase - The use case to apply, with `id` and `preset`
 */
export const applyUseCasePreset = (id, preset) => {
  const {
    polyCount,
    polygonType,
    material,
    isSmartLowPoly,
    isUnlimitedPolycount,
    extraQuality,
    provider,
    targetPolycount: presetTargetPolycount,
  } = preset

  const generationAction = threeDStudioStore.getState().schema.generationAction
  const modelSettings = threeDStudioStore.getState().modelSettings
  const modelSettingsPatch = {
    ...modelSettings,
    ...(polyCount !== undefined && { polyCount }),
    ...(polygonType !== undefined && { polygonType }),
    ...(material !== undefined && { material }),
    ...(presetTargetPolycount !== undefined && { targetPolycount: presetTargetPolycount }),
    isSmartLowPoly,
    isUnlimitedPolycount,
    ...(extraQuality !== undefined && { extraQuality }),
    // The patch starts from the current settings, so a leftover extraQuality would
    // survive into a LowPoly preset and keep narrowing the engines to Meshy.
    ...(isSmartLowPoly && { extraQuality: false }),
    selectedUseCase: id,
  }

  if (provider) {
    setGenerationProvider(provider)
  }

  const generationModelSchema = getNextGenerationSchema(generationAction, modelSettingsPatch)

  const range = resolveActivePolycountRange(generationModelSchema, {
    polygonType: modelSettingsPatch.polygonType,
    isSmartLowPoly: modelSettingsPatch.isSmartLowPoly,
  })

  // A preset that names no polycount leaves polyCount/targetPolycount alone; the
  // Polycount control re-snaps them when the range changes under it.
  if (presetTargetPolycount === undefined && polyCount !== undefined && range) {
    const { minimum, maximum, default: defaultValue } = range
    modelSettingsPatch.targetPolycount = {
      [POLY_COUNT.LOW]: minimum,
      [POLY_COUNT.MEDIUM]: defaultValue,
      [POLY_COUNT.HIGH]: maximum,
    }[polyCount]
  }

  // The Polycount control only re-clamps when the range changes, so re-applying a
  // preset whose target exceeds the range (Low Poly 20000 vs image-to-3D's 15000
  // cap) would otherwise leave the out-of-range value in place.
  if (presetTargetPolycount !== undefined && range?.minimum != null && range?.maximum != null) {
    const { minimum, maximum } = range
    const targetPolycount = Math.min(Math.max(presetTargetPolycount, minimum), maximum)
    const presetPolyCount = polyCountForValue(targetPolycount, range)

    modelSettingsPatch.targetPolycount = targetPolycount
    if (presetPolyCount) {
      modelSettingsPatch.polyCount = presetPolyCount
    }
  }

  threeDStudioStore.setState((state) => ({
    schema: {
      ...state.schema,
      ...(generationModelSchema && { generationModelSchema }),
    },
    modelSettings: { ...state.modelSettings, ...modelSettingsPatch },
  }))
}
